import i18next from "i18next";

export type PackingKitItem = {
  name: string;
  category: string;
};

export type PackingKit = {
  id: string;
  name: string;
  icon: string;
  items: PackingKitItem[];
};

const PACKING_KITS: PackingKit[] = [
  {
    id: "beach",
    name: "Beach Vacation",
    icon: "beach",
    items: [
      { name: "Swimsuit", category: "Clothing" },
      { name: "Sandals", category: "Clothing" },
      { name: "Shorts", category: "Clothing" },
      { name: "T-shirts", category: "Clothing" },
      { name: "Sun hat", category: "Clothing" },
      { name: "Cover-up", category: "Clothing" },
      { name: "Sunscreen", category: "Toiletries" },
      { name: "After-sun lotion", category: "Toiletries" },
      { name: "Sunglasses", category: "Accessories" },
      { name: "Beach towel", category: "Accessories" },
      { name: "Waterproof phone pouch", category: "Accessories" },
      { name: "Snorkel & mask", category: "Accessories" },
      { name: "Beach bag", category: "Accessories" },
      { name: "Book or e-reader", category: "Entertainment" },
      { name: "Portable speaker", category: "Entertainment" },
    ],
  },
  {
    id: "business",
    name: "Business Trip",
    icon: "briefcase-outline",
    items: [
      { name: "Suit / Blazer", category: "Clothing" },
      { name: "Dress shirt", category: "Clothing" },
      { name: "Dress shoes", category: "Clothing" },
      { name: "Belt", category: "Clothing" },
      { name: "Socks", category: "Clothing" },
      { name: "Underwear", category: "Clothing" },
      { name: "Pyjamas", category: "Clothing" },
      { name: "Laptop", category: "Electronics" },
      { name: "Laptop charger", category: "Electronics" },
      { name: "Phone charger", category: "Electronics" },
      { name: "Business cards", category: "Documents" },
      { name: "Passport / ID", category: "Documents" },
      { name: "Boarding pass", category: "Documents" },
      { name: "Toothbrush & toothpaste", category: "Toiletries" },
      { name: "Deodorant", category: "Toiletries" },
    ],
  },
  {
    id: "camping",
    name: "Camping & Hiking",
    icon: "tent",
    items: [
      { name: "Tent", category: "Shelter & Sleep" },
      { name: "Sleeping bag", category: "Shelter & Sleep" },
      { name: "Sleeping pad", category: "Shelter & Sleep" },
      { name: "Pillow", category: "Shelter & Sleep" },
      { name: "Hiking boots", category: "Clothing" },
      { name: "Moisture-wicking shirts", category: "Clothing" },
      { name: "Rain jacket", category: "Clothing" },
      { name: "Warm fleece", category: "Clothing" },
      { name: "Hiking socks", category: "Clothing" },
      { name: "Headlamp", category: "Gear" },
      { name: "Multi-tool / Knife", category: "Gear" },
      { name: "Water bottle", category: "Gear" },
      { name: "First aid kit", category: "Gear" },
      { name: "Map / Compass", category: "Gear" },
      { name: "Sunscreen", category: "Toiletries" },
      { name: "Insect repellent", category: "Toiletries" },
    ],
  },
  {
    id: "weekend",
    name: "Weekend Getaway",
    icon: "bag-suitcase-outline",
    items: [
      { name: "T-shirts", category: "Clothing" },
      { name: "Jeans / Pants", category: "Clothing" },
      { name: "Underwear", category: "Clothing" },
      { name: "Socks", category: "Clothing" },
      { name: "Casual shoes", category: "Clothing" },
      { name: "Pyjamas", category: "Clothing" },
      { name: "Toothbrush & toothpaste", category: "Toiletries" },
      { name: "Deodorant", category: "Toiletries" },
      { name: "Shampoo", category: "Toiletries" },
      { name: "Phone charger", category: "Electronics" },
      { name: "Wallet", category: "Essentials" },
      { name: "Keys", category: "Essentials" },
    ],
  },
  {
    id: "winter",
    name: "Winter Holiday",
    icon: "snowflake",
    items: [
      { name: "Winter coat", category: "Clothing" },
      { name: "Thermal underwear", category: "Clothing" },
      { name: "Sweaters", category: "Clothing" },
      { name: "Warm pants", category: "Clothing" },
      { name: "Warm socks", category: "Clothing" },
      { name: "Winter boots", category: "Clothing" },
      { name: "Gloves", category: "Accessories" },
      { name: "Scarf", category: "Accessories" },
      { name: "Beanie / Hat", category: "Accessories" },
      { name: "Sunglasses", category: "Accessories" },
      { name: "Lip balm", category: "Toiletries" },
      { name: "Moisturizer", category: "Toiletries" },
      { name: "Hand warmers", category: "Gear" },
      { name: "Thermos", category: "Gear" },
    ],
  },
  {
    id: "international",
    name: "International Travel",
    icon: "airplane",
    items: [
      { name: "Passport", category: "Documents" },
      { name: "Visa (if needed)", category: "Documents" },
      { name: "Travel insurance docs", category: "Documents" },
      { name: "Boarding pass", category: "Documents" },
      { name: "Hotel confirmation", category: "Documents" },
      { name: "Travel adapter", category: "Electronics" },
      { name: "Phone charger", category: "Electronics" },
      { name: "Portable battery pack", category: "Electronics" },
      { name: "Noise-cancelling headphones", category: "Electronics" },
      { name: "Neck pillow", category: "Travel Comfort" },
      { name: "Eye mask", category: "Travel Comfort" },
      { name: "Earplugs", category: "Travel Comfort" },
      { name: "Reusable water bottle", category: "Travel Comfort" },
      { name: "Medications", category: "Health" },
      { name: "Hand sanitizer", category: "Health" },
    ],
  },
];

export const getTranslatedKits = (): PackingKit[] =>
  PACKING_KITS.map((kit) => ({
    ...kit,
    name: i18next.t(`kits.${kit.id}.name`),
    items: kit.items.map((_item, idx) => ({
      name: i18next.t(`kits.${kit.id}.items.${idx}.name`),
      category: i18next.t(`kits.${kit.id}.items.${idx}.category`),
    })),
  }));
